import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { TaskGeneral, TaskStatusMapped } from '../../../models/owner-portal/taskGeneral';
import { ListingGeneral } from '../../../models/owner-portal/listingGeneral';
import { ReservationGeneral } from '../../../models/owner-portal/reservationGeneral';
import { CaseGeneral } from '../../../models/owner-portal/caseGeneral';
import MenuPopover from '../../../components/menu-popover/MenuPopover';
import { Button } from '../../../components/ui/Button';
import { FDateFormat, fDate } from '../../../utils/formatTime';
import Label from '../../../components/label/Label';
import TaskDetails from './TasksDetails';
import useResponsive from '../../../hooks/useResponsive';
import { TableCell, TableRow } from '../../../components/ui/Table';
import { PATH_HOME } from '../../../routes/paths';
import { cn } from '../../../lib/utils';

type Props = {
  //   isSessionUser: boolean;
  row: TaskGeneral;
  selected: boolean;
  onViewRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
  dense?: boolean;
  showColumns: Set<keyof TaskGeneral>;
};

export function TaskTableRow({
  row,
  selected,
  onViewRow: _onViewRow,
  onSelectRow: _onSelectRow,
  onDeleteRow: _onDeleteRow,
  dense = true,
  showColumns
}: Props) {
  const {
    assignee_name,
    status_mapped,
    dueDate,
    related_name,
    title,
    last_modified_date_ISO,
    unit_id
  } = row;

  const isDesktop = useResponsive('up', 'md');

  const dateFormat = useMemo(() => {
    if (isDesktop) {
      return FDateFormat.localized_date_time_full;
    }
    return FDateFormat.localized_us_numeric_month_day_year;
  }, [isDesktop]);

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const denseHeight = dense ? 52 : 72;

  return (
    <>
      <TableRow
        onClick={handleOpenPopover}
        className={cn('cursor-pointer', selected && 'bg-muted')}
        style={{ height: denseHeight }}
      >
        {showColumns.has('title') && (
          <TableCell>
            <Button
              variant="link"
              className="h-auto p-0 font-medium text-primary dark:text-secondary hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPopover(e as unknown as React.MouseEvent<HTMLElement>);
              }}
            >
              {title}
            </Button>
          </TableCell>
        )}
        {showColumns.has('status_mapped') && (
          <TableCell>
            <Label
              variant="soft"
              color={
                //TODO: What should be mapped here? Once decided, move the color lookup to listingContants.ts.
                (status_mapped === TaskStatusMapped.in_progress && 'primary') ||
                (status_mapped === TaskStatusMapped.not_started && 'warning') ||
                (status_mapped === TaskStatusMapped.completed && 'success') ||
                (status_mapped === TaskStatusMapped.deffered && 'error') ||
                'info'
              }
            >
              {status_mapped}
            </Label>
          </TableCell>
        )}
        {showColumns.has('assignee_name') && <TableCell>{assignee_name}</TableCell>}
        {showColumns.has('related_name') && (
          <TableCell className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
            {row.related instanceof ListingGeneral && (
              <NavLink
                to={PATH_HOME.property.view(row.related.id)}
                className="text-sm font-medium text-primary dark:text-secondary hover:underline truncate"
              >
                {isDesktop ? related_name || '-' : unit_id || '-'}
              </NavLink>
            )}
            {row.related instanceof ReservationGeneral && (
              <NavLink
                to={PATH_HOME.booking.view(row.related.id)}
                className="text-sm font-medium text-primary dark:text-secondary hover:underline truncate"
              >
                {isDesktop ? related_name || '-' : unit_id || '-'}
              </NavLink>
            )}
            {row.related instanceof CaseGeneral && (
              <NavLink
                to={PATH_HOME.case.root}
                className="text-sm font-medium text-primary dark:text-secondary hover:underline truncate"
              >
                {isDesktop ? related_name || '-' : unit_id || '-'}
              </NavLink>
            )}
            {!row.related && (
              <span className="text-muted-foreground">
                {(isDesktop ? related_name : unit_id) || '-'}
              </span>
            )}
          </TableCell>
        )}
        {showColumns.has('dueDate') && (
          <TableCell className="whitespace-nowrap">{fDate(dueDate, dateFormat)}</TableCell>
        )}
        {showColumns.has('last_modified_date_ISO') && (
          <TableCell className="whitespace-nowrap text-right">
            {fDate(last_modified_date_ISO, dateFormat)}
          </TableCell>
        )}
        {/* <TableCell align="right">
                <IconButton color={openPopover ? 'inherit' : 'default'} onClick={handleOpenPopover}>
                    <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
            </TableCell> */}
      </TableRow>

      <MenuPopover open={openPopover} onClose={handleClosePopover} arrow="right-top">
        <TaskDetails task={row} />
      </MenuPopover>
    </>
  );
}
